import { motion, useMotionValue } from 'framer-motion';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import TinderCard from 'react-tinder-card';
import { pushEventToParent } from '../../../Utils/eventUtils';
import { captureException } from '../../../Utils/sentryUtils';
import NudgeCard from '../NudgeCard';
import Header from './Header';
import styles from './index.module.css';
import StackEnd from './StackEnd';
import { NudgeBody, NudgeStackProps } from './types';

const useWindowDimensions = () => {
  const [windowDimensions, setWindowDimensions] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    handleResize();

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  return windowDimensions;
};

const JFSNudgeStack: React.FC<NudgeStackProps> = ({
  data = [],
  onSwipeLeft,
  onSwipeRight,
  onClose,
  allowSwipe = true,
  header,
  stackEnd,
}) => {
  const [currentNudgeIndex, setCurrentNudgeIndex] = useState(0);
  const [nextNudgeIndex, setNextNudgeIndex] = useState(currentNudgeIndex + 1);
  const [isExternalLoading, setIsExternalLoading] = useState(false);
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();

  const currentNudge = data[currentNudgeIndex];
  const nextNudge = data[nextNudgeIndex];
  const thirdNudge = data[currentNudgeIndex + 2];

  const currentNudgeCardTranslateX = useMotionValue(0);

  useEffect(() => {
    currentNudgeCardTranslateX.set(0);
    setNextNudgeIndex(currentNudgeIndex + 1);
    if (currentNudge !== undefined) {
      try {
        pushEventToParent({
          eventCode: 'NUDGE_VIEWED_' + currentNudge.category,
        });
      } catch (error) {
        captureException(error);
      }
    }
  }, [currentNudgeIndex, currentNudgeCardTranslateX]);

  const handleReset = () => {
    setCurrentNudgeIndex(0);
  };

  const handleUndo = () => {
    if (currentNudgeIndex === 0) return;
    setCurrentNudgeIndex(currentNudgeIndex - 1);
  };

  // Clamped: appended nudges can change data.length under the user, and a
  // negative count would keep the (empty) card area rendered instead of
  // handing over to the end card.
  const numberOfNudgesLeft = Math.max(0, data.length - currentNudgeIndex);

  const childRefs = useMemo(
    () =>
      Array(data.length)
        .fill(0)
        .map(() => React.createRef<any>()),
    [data.length]
  );

  const updateCurrentIndex = (val: number) => {
    setCurrentNudgeIndex(val);
  };

  const onSwipe = useCallback(
    (nudge: any) => {
      updateCurrentIndex(currentNudgeIndex + 1);
    },
    [currentNudgeIndex]
  );

  const containerStyle = {
    height: `${windowHeight}px`,
    maxHeight: `${windowHeight}px`,
    '--window-height': `${windowHeight}px`,
    '--window-width': `${windowWidth}px`,
  } as React.CSSProperties;

  return (
    <div className={styles.nudgeStackWrapper} style={containerStyle}>
      <div className={styles.nudgeStackContainer}>
        {header?.visible && (
          <Header
            title={numberOfNudgesLeft !== 0 ? (currentNudge?.title ?? '') : ''}
            numberOfNudgesLeft={numberOfNudgesLeft}
            currentNudgeIndex={currentNudgeIndex}
            onReset={handleReset}
            onUndo={handleUndo}
            onClose={onClose}
            showNumberOfNudgesLeft={header.showNumberOfNudgesLeft}
            showResetIconButton={header.showResetIconButton}
            showUndoButton={header.showUndoButton}
            showCloseButton={header.showCloseButton}
            link={(currentNudge?.body as NudgeBody)?.appLink ?? ''}
          />
        )}

        {numberOfNudgesLeft > 0 && (
          <div className={styles.cardsContainer}>
            <div className={styles.cardWrapper}>
              {thirdNudge && (
                <motion.div
                  key={thirdNudge.nudgeId || currentNudgeIndex + 2}
                  className={styles.thirdCard}
                  initial={{ scale: 0.88, opacity: 0 }}
                  animate={{ scale: 1, opacity: 0.3 }}
                  transition={{
                    type: 'tween',
                    duration: 0.22,
                    ease: 'easeOut',
                  }}
                >
                  <NudgeCard nudge={thirdNudge} />
                </motion.div>
              )}

              {nextNudge && (
                <motion.div
                  key={nextNudge.nudgeId || nextNudgeIndex}
                  className={styles.nextCard}
                  initial={{ scale: 0.93, opacity: 0.4 }}
                  animate={{ scale: 1, opacity: 0.7 }}
                  transition={{
                    type: 'tween',
                    duration: 0.22,
                    ease: 'easeOut',
                  }}
                >
                  <NudgeCard nudge={nextNudge} />
                </motion.div>
              )}

              {currentNudge && (
                <TinderCard
                  ref={childRefs[currentNudgeIndex]}
                  key={currentNudge.nudgeId || currentNudgeIndex}
                  onSwipe={(dir) => {
                    if (dir === 'right') {
                      onSwipeRight?.(currentNudge);
                    } else if (dir === 'left') {
                      onSwipeLeft?.(currentNudge);
                    }
                    onSwipe(currentNudge);
                  }}
                  onCardLeftScreen={() => {}}
                  flickOnSwipe={true}
                  swipeRequirementType='position'
                  swipeThreshold={100}
                  className={styles.currentCard}
                >
                  <NudgeCard
                    nudge={currentNudge}
                    onLoadingChange={setIsExternalLoading}
                  />
                </TinderCard>
              )}
            </div>
          </div>
        )}
        {stackEnd?.showReset && numberOfNudgesLeft === 0 && (
          <div className={styles.cardsContainer}>
            <div className={styles.cardWrapper}>
              <StackEnd
                onClose={onClose}
                emoji={stackEnd.emoji}
                heading={stackEnd.heading}
              />
            </div>
          </div>
        )}
      </div>

      {isExternalLoading && (
        <div className={styles.loadingOverlay}>
          <div className={styles.loadingContent}>
            <div className={styles.loadingText}>
              {(currentNudge?.body as NudgeBody)?.loadingText ??
                'You are leaving Jio Finance Platform & Service Limited'}
            </div>
            <div className={styles.progressBarContainer}>
              <div className={styles.progressBar}></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JFSNudgeStack;
